import type { I18n } from '@learnforge/i18n';

export interface DropdownOption<T extends string> {
  value: T;
  labelKey: string;
}

export interface CreateDropdownRowOptions<T extends string> {
  id: string;
  i18n: I18n<string>;
  options: readonly DropdownOption<T>[];
  value: T;
  onChange(v: T): void;
  // Optional i18n key. When provided, its translation is set as `aria-label` on the
  // <select> so screen readers have an accessible name (callers typically pass the
  // surrounding panel's title key, e.g. 'panel.figure' or 'panel.scene').
  labelKey?: string;
}

export interface DropdownRowHandle<T extends string> {
  element: HTMLElement;
  setValue(v: T): void;
  getValue(): T;
}

export function createDropdownRow<T extends string>(
  opts: CreateDropdownRowOptions<T>,
): DropdownRowHandle<T> {
  const row = document.createElement('div');
  row.className = 'dropdown-row';
  row.dataset.rowKind = 'dropdown';
  row.innerHTML = `<select class="fig-select" id="dropdown_${opts.id}"></select>`;
  const select = row.querySelector('select') as HTMLSelectElement;

  for (const o of opts.options) {
    const optEl = document.createElement('option');
    optEl.value = o.value;
    optEl.dataset.labelKey = o.labelKey;
    optEl.textContent = opts.i18n.t(o.labelKey);
    select.appendChild(optEl);
  }
  select.value = opts.value;

  const updateAriaLabel = () => {
    if (opts.labelKey) {
      select.setAttribute('aria-label', opts.i18n.t(opts.labelKey));
    }
  };
  updateAriaLabel();

  select.addEventListener('change', () => {
    opts.onChange(select.value as T);
  });

  opts.i18n.subscribe(() => {
    for (const opt of Array.from(select.options)) {
      const key = opt.dataset.labelKey;
      if (key) opt.textContent = opts.i18n.t(key);
    }
    updateAriaLabel();
  });

  return {
    element: row,
    setValue(v) {
      select.value = v;
    },
    getValue() {
      return select.value as T;
    },
  };
}
